import React from "react";
import Navbar from "../components/Navbar";
import { AlertTriangle, ShieldOff, Gavel, Briefcase } from "lucide-react";

const points = [
  {
    icon: Gavel,
    title: "Not Legal Advice",
    body: "GovBidIQ does not provide legal advice. All references to the Federal Acquisition Regulation (FAR), DFARS, agency-specific supplements, or any contractual clauses are presented for informational and educational purposes only. Users must consult licensed counsel for any binding legal interpretation, contract negotiation, or compliance determination.",
  },
  {
    icon: Briefcase,
    title: "Not Procurement Consulting",
    body: "GovBidIQ is a decision-support and educational analysis tool. It does not constitute, replace, or substitute for procurement consulting services, capture management consulting, proposal management services, or any other professional contracting service. Users remain responsible for independent business judgment, capture decisions, and proposal preparation.",
  },
  {
    icon: ShieldOff,
    title: "Not a Guarantee of Award",
    body: "GovBidIQ does not guarantee, predict, or warrant contract award, evaluation outcomes, or competitive positioning of any kind. Win probability indicators, scores, and recommendations are estimates produced by AI-assisted analysis and may be incomplete, inaccurate, or out of date. No representation is made that following GovBidIQ recommendations will result in a contract award.",
  },
  {
    icon: AlertTriangle,
    title: "Decision-Support & Educational Use Only",
    body: "GovBidIQ outputs are intended solely to support a contractor's internal Bid / No-Bid analysis. Reports are an aid to decision-making — not a replacement for it. Users assume full responsibility for any business, financial, contractual, regulatory, or strategic action taken in reliance on GovBidIQ outputs.",
  },
];

export default function Disclaimer() {
  return (
    <div className="grain min-h-screen">
      <Navbar />

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1000px] mx-auto px-6 py-20">
          <div className="tick-label text-sky-400 mb-4">/ Disclaimer &amp; Terms</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl text-slate-100 leading-[1.05] mb-6">
            Important notices about how GovBidIQ outputs may and may not be used.
          </h1>
          <p className="text-slate-300 text-base leading-relaxed max-w-3xl">
            Please read carefully before using GovBidIQ analysis outputs to inform any contracting,
            proposal, financial, or business decision.
          </p>
        </div>
      </section>

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1000px] mx-auto px-6 py-16">
          <div className="space-y-px bg-[var(--border-soft)]">
            {points.map((p, i) => (
              <div key={p.title} className="bg-[var(--bg-primary)] p-7">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-sky-500/40 bg-sky-500/5 flex items-center justify-center flex-shrink-0">
                    <p.icon className="w-5 h-5 text-sky-400" strokeWidth={1.6} />
                  </div>
                  <div>
                    <div className="font-mono text-[10px] text-slate-500 tracking-[0.2em] mb-1">
                      {String(i + 1).padStart(2, "0")} · NOTICE
                    </div>
                    <h2 className="font-display font-bold text-slate-100 text-lg mb-3">{p.title}</h2>
                    <p className="text-sm text-slate-300 leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-[var(--border-soft)]">
        <div className="max-w-[1000px] mx-auto px-6 py-16 space-y-8">
          <div>
            <h3 className="font-display font-bold text-slate-100 mb-3">Acceptable Use</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              GovBidIQ is intended for use by federal contractors, capture professionals, business
              development teams, proposal managers, executives, and authorized representatives of
              U.S. government contractors evaluating potential opportunities. Users are responsible
              for ensuring that documents submitted to GovBidIQ do not contain classified
              information, controlled unclassified information (CUI) outside the user&apos;s authorized
              handling, source-selection-sensitive material, or any data they are not authorized to
              process via third-party AI services.
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-slate-100 mb-3">Limitation of Liability</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              To the maximum extent permitted by law, GovBidIQ and its operators disclaim all
              warranties, express or implied, and shall not be liable for any damages — direct,
              indirect, incidental, consequential, special, or punitive — arising from the use of,
              or inability to use, the platform or any of its outputs. Users assume all risk
              associated with reliance on GovBidIQ analysis.
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-slate-100 mb-3">AI-Generated Content</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              GovBidIQ reports are produced by a large language model (Claude Sonnet 4.5). AI
              outputs may contain inaccuracies, omissions, hallucinated clauses, misattributed
              requirements, or stale assumptions. Independent verification of every clause, date,
              requirement, and conclusion is the responsibility of the user prior to making any
              business or contractual decision.
            </p>
          </div>
          <div>
            <h3 className="font-display font-bold text-slate-100 mb-3">Refund &amp; Use Policy</h3>
            <p className="text-sm text-slate-300 leading-relaxed">
              GovBidIQ paid reports are delivered upon analysis completion. Because each report
              requires AI compute resources at the time of purchase, paid reports are
              non-refundable once generated. If your analysis fails to complete due to a technical
              error on our side, contact support for a regeneration or refund.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
